import { importAesKey } from '../crypto/aes.ts';
import {
  ACCESS_PROTOCOL_DOMAIN,
  ACCESS_PROTOCOL_VERSION,
  ACCESS_PURPOSE_SESSION_SHARE,
  ACCESS_PURPOSE_SHARE,
  ACCESS_THRESHOLD,
  combineAccessRootShares,
  fingerprintAccessPublicKey,
  generateAccessKeyPair,
  hashAccessManifest,
  hashSignedOrderPayload,
  openSessionAccessShare,
  rewrapAccessShareForSession,
  sealAccessRootShare,
  splitAccessRoot,
  unwrapPeriodAccessKey,
  unwrapThreadEpochForPeriod,
  type AccessRootSharePackage,
  type AccessSessionShareSealContext,
  type AccessShareSealContext
} from '../crypto/access.ts';
import { decryptMessageText } from '../crypto/messagePayload.ts';
import { accessApi } from './api.ts';
import { createIndexedDbAccessKeyStore, type AccessKeyStore } from './keyStore.ts';

export function createAccessCeremony(store: AccessKeyStore = createIndexedDbAccessKeyStore()) {
  async function ensureCustodianKey() {
    const existing = (await store.listKeys()).find((row) => row.kind === 'custodian');
    if (existing) return existing;
    const pair = await generateAccessKeyPair();
    const record = {
      kind: 'custodian' as const,
      publicKeyFingerprint: pair.publicKeyFingerprint,
      publicKeyJwk: pair.publicKeyJwk,
      privateKey: pair.privateKey,
      createdAt: new Date().toISOString()
    };
    await store.putKey(record);
    await accessApi.registerLabKey(pair.publicKeyJwk);
    return record;
  }

  async function ensureSessionKey(sessionId?: string) {
    if (sessionId) {
      const existing = await store.getSessionKey(sessionId);
      if (existing) return existing;
    }
    const pair = await generateAccessKeyPair();
    const record = {
      kind: 'session' as const,
      publicKeyFingerprint: pair.publicKeyFingerprint,
      publicKeyJwk: pair.publicKeyJwk,
      privateKey: pair.privateKey,
      sessionId,
      createdAt: new Date().toISOString()
    };
    await store.putKey(record);
    return record;
  }

  async function openInvestigatorSession(orderId: string) {
    const key = await ensureSessionKey();
    const created = await accessApi.createSession({
      orderId,
      sessionPublicKeyJwk: key.publicKeyJwk
    });
    await store.putKey({
      ...key,
      sessionId: created.session.id
    });
    return created.session;
  }

  async function destroySessionKey(sessionId: string) {
    const existing = await store.getSessionKey(sessionId);
    if (existing) await store.deleteKeyByFingerprint(existing.publicKeyFingerprint);
  }

  async function depositShares(accessRoot: Uint8Array, targetUserId: string) {
    const pendingBody = await accessApi.pendingDeposit();
    const pending = pendingBody.pending;
    if (!pending) throw new Error('No draft custody set is waiting for this account.');
    const periodId = pending.suggestedPeriodId;
    if (!periodId) throw new Error('Could not name the current access period.');
    const shares = splitAccessRoot(accessRoot);
    const createdAt = new Date().toISOString();
    const custodians = [];
    for (const row of pending.custodians) {
      if (!row.custodyPublicKeyJwk) throw new Error('A custodian is missing a public key.');
      custodians.push({
        custodianId: row.id,
        index: row.index,
        role: row.role,
        custodyPublicKeyFingerprint: await fingerprintAccessPublicKey(row.custodyPublicKeyJwk)
      });
    }
    custodians.sort((left, right) => left.index - right.index);
    const manifest = {
      protocolVersion: ACCESS_PROTOCOL_VERSION,
      protocolDomain: ACCESS_PROTOCOL_DOMAIN,
      custodySetId: pending.custodySetId,
      custodySetVersion: pending.version,
      targetUserId,
      threshold: 2,
      total: 3,
      periodId,
      createdAt,
      custodians
    };
    const manifestHash = await hashAccessManifest(manifest);
    const sealedShares = [];
    for (const row of pending.custodians) {
      const share = shares.find((item) => item.index === row.index);
      if (!share || !row.custodyPublicKeyJwk) {
        throw new Error('Missing access root share for a custodian.');
      }
      const context: AccessShareSealContext = {
        purpose: ACCESS_PURPOSE_SHARE,
        protocolDomain: ACCESS_PROTOCOL_DOMAIN,
        protocolVersion: ACCESS_PROTOCOL_VERSION,
        custodySetId: pending.custodySetId,
        custodySetVersion: pending.version,
        manifestHash,
        custodianId: row.id,
        role: row.role,
        index: row.index,
        custodyPublicKeyFingerprint: await fingerprintAccessPublicKey(row.custodyPublicKeyJwk),
        periodId
      };
      sealedShares.push({
        custodianId: row.id,
        envelope: await sealAccessRootShare(share, row.custodyPublicKeyJwk, context)
      });
    }
    await accessApi.depositShares({
      custodySetId: pending.custodySetId,
      periodId,
      manifest,
      sealedShares
    });
    return { custodySetId: pending.custodySetId, periodId };
  }

  async function releaseForSession(sessionId: string) {
    const sessionBody = await accessApi.getSession(sessionId);
    const assignment = sessionBody.session;
    const share = await accessApi.getMyShare(assignment.custodySetId);
    const custodianKey = await ensureCustodianKey();
    const custodyFp = await fingerprintAccessPublicKey(share.custodyPublicKeyJwk);
    if (custodyFp !== custodianKey.publicKeyFingerprint) {
      throw new Error('This browser does not hold the lab key registered for this custodian role.');
    }
    const sessionKey = sessionBody.session.sessionPublicKeyJwk;
    if (!sessionKey) throw new Error('The session public key is missing.');
    const sessionFingerprint = await fingerprintAccessPublicKey(sessionKey);
    const signedHash = await hashSignedOrderPayload(sessionBody.signedOrder.payload);
    const custodyContext: AccessShareSealContext = {
      purpose: ACCESS_PURPOSE_SHARE,
      protocolDomain: ACCESS_PROTOCOL_DOMAIN,
      protocolVersion: ACCESS_PROTOCOL_VERSION,
      custodySetId: share.custodySetId,
      custodySetVersion: Number((share.activationManifest as { custodySetVersion?: number })?.custodySetVersion || 1),
      manifestHash: await hashAccessManifest(share.activationManifest as Parameters<typeof hashAccessManifest>[0]),
      custodianId: share.custodianId,
      role: share.role,
      index: share.index,
      custodyPublicKeyFingerprint: share.custodyPublicKeyFingerprint,
      periodId: share.periodId
    };
    const sessionContext: AccessSessionShareSealContext = {
      ...custodyContext,
      purpose: ACCESS_PURPOSE_SESSION_SHARE,
      orderId: sessionBody.session.orderId,
      sessionId: sessionBody.session.id,
      sessionPublicKeyFingerprint: sessionFingerprint,
      signedOrderHash: signedHash,
      expiresAt: sessionBody.session.expiresAt
    };
    const sealedShare = await rewrapAccessShareForSession({
      custodyEnvelope: share.envelope,
      custodianPrivateKey: custodianKey.privateKey,
      custodyContext,
      sessionPublicKey: sessionKey,
      sessionContext,
      signedOrder: sessionBody.signedOrder
    });
    return accessApi.release(sessionId, sealedShare);
  }

  async function openBundle(sessionId: string) {
    const view = await accessApi.getView(sessionId);
    const sessionKey = await store.getSessionKey(sessionId);
    if (!sessionKey) {
      throw new Error('This workstation no longer holds the session key. Open the session from this browser.');
    }
    const packages: AccessRootSharePackage[] = [];
    for (const share of view.sealedShares.slice(0, ACCESS_THRESHOLD)) {
      packages.push(await openSessionAccessShare(
        share.envelope,
        sessionKey.privateKey,
        share.context as AccessSessionShareSealContext
      ));
      if (packages.length >= ACCESS_THRESHOLD) break;
    }
    if (packages.length < ACCESS_THRESHOLD) {
      throw new Error('Two of three session shares are required.');
    }
    const accessRoot = combineAccessRootShares(packages);
    const corpus = await accessApi.getCorpus(sessionId);
    const threadIds = corpus.threads.map((thread) => thread.id);
    const bundle = threadIds.length
      ? await accessApi.loadThreads(sessionId, threadIds)
      : {
        threadIds: [],
        roomTypes: [],
        threads: [],
        periodIds: [],
        periodEnvelopes: [],
        accessPeriodWraps: [],
        ciphertext: [],
        omissions: corpus.omissions || [],
        limitations: corpus.limitations || []
      };
    const paks = new Map<string, Uint8Array>();
    for (const envelope of bundle.periodEnvelopes) {
      const periodId = (envelope as { periodId?: string }).periodId;
      if (!periodId) continue;
      paks.set(periodId, await unwrapPeriodAccessKey(envelope, accessRoot, {
        targetUserId: view.order.targetUserId,
        periodId
      }));
    }
    const threadKeys = new Map<number, Uint8Array>();
    for (const item of bundle.accessPeriodWraps) {
      const wrap = item.wrap as { periodId: string };
      const pak = paks.get(wrap.periodId);
      if (!pak) continue;
      threadKeys.set(item.threadId, await unwrapThreadEpochForPeriod(item.wrap, pak, {
        targetUserId: view.order.targetUserId,
        periodId: wrap.periodId
      }));
    }
    const threads = [];
    for (const thread of corpus.threads) {
      const meta = bundle.threads?.find((row) => row.id === thread.id) || thread;
      const keyBytes = threadKeys.get(thread.id);
      const key = keyBytes ? await importAesKey(keyBytes) : null;
      const messages = [];
      for (const item of bundle.ciphertext.filter((row) => row.threadId === thread.id)) {
        let plaintext = item.ciphertext;
        if (key) {
          try {
            plaintext = await decryptMessageText(key, item.ciphertext);
          } catch {
            plaintext = '[Could not decrypt]';
          }
        } else if (meta.encryptionEnabled) {
          plaintext = '[No period wrap for this thread]';
        }
        messages.push({
          id: item.messageId,
          time: item.time,
          type: item.type,
          text: plaintext,
          senderLabel: item.senderLabel || 'Correspondent'
        });
      }
      threads.push({
        id: thread.id,
        name: thread.name,
        roomType: meta.roomType || thread.roomType,
        correspondents: meta.correspondents || [],
        messages
      });
    }
    accessRoot.fill(0);
    return {
      view,
      threads,
      omissions: bundle.omissions || corpus.omissions || [],
      limitations: bundle.limitations || corpus.limitations || []
    };
  }

  return {
    ensureCustodianKey,
    ensureSessionKey,
    destroySessionKey,
    openInvestigatorSession,
    depositShares,
    releaseForSession,
    openBundle
  };
}

export type AccessCeremony = ReturnType<typeof createAccessCeremony>;
